import { useEffect, useRef, useState } from 'react';
import { useTheme } from './ThemeProvider.js';
import type { SceneKey } from '@classy/theme-sdk';
import styles from './BgLayer.module.css';

export interface BgLayerProps {
  /** Active scene — drives which background image is in front. */
  scene: SceneKey;
}

/**
 * Crossfading background layer. Two image slots that swap z/opacity when
 * the theme or scene changes — produces the same paper-fade effect the
 * v1 app has between scenes/themes.
 */
export function BgLayer({ scene }: BgLayerProps) {
  const theme = useTheme();
  const targetUrl = theme.getBackground(scene);

  const [layers, setLayers] = useState<{ a: string; b: string | null; activeSlot: 'a' | 'b' }>(
    () => ({ a: targetUrl, b: null, activeSlot: 'a' }),
  );

  const lastUrlRef = useRef(targetUrl);

  useEffect(() => {
    if (targetUrl === lastUrlRef.current) return;
    lastUrlRef.current = targetUrl;
    setLayers((prev) =>
      prev.activeSlot === 'a'
        ? { a: prev.a, b: targetUrl, activeSlot: 'b' }
        : { a: targetUrl, b: prev.b, activeSlot: 'a' },
    );
  }, [targetUrl]);

  return (
    <div className={styles.bgLayer} aria-hidden="true">
      <img src={layers.a} alt="" className={styles.bgImg} data-active={layers.activeSlot === 'a'} />
      {layers.b ? (
        <img
          src={layers.b}
          alt=""
          className={styles.bgImg}
          data-active={layers.activeSlot === 'b'}
        />
      ) : null}
    </div>
  );
}
